/**
 * Admin Dashboard
 * Overview of all driver activity
 */

import { Package, Clock, Users, TrendingUp } from 'lucide-react'
import { Card, Badge } from '../ui'
import { useAppStore } from '../../stores'
import { formatDate } from '../../utils/helpers'

// =============================================================================
// STAT CARD COMPONENT
// =============================================================================

interface StatCardProps {
  title: string
  value: string | number
  icon: React.ReactNode
  trend?: string
}

function StatCard({ title, value, icon, trend }: StatCardProps) {
  return (
    <Card className="p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-medium text-gray-900 mt-1">{value}</p>
          {trend && (
            <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
              <TrendingUp className="w-3 h-3" />
              {trend}
            </p>
          )}
        </div>
        <div className="p-2 bg-gray-100 rounded-lg">
          {icon}
        </div>
      </div>
    </Card>
  )
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function AdminDashboard() {
  const { deliveries, clockEntries } = useAppStore()
  
  // Calculate stats
  const todayDeliveries = deliveries.filter(d => {
    const today = new Date()
    const entryDate = new Date(d.timestamp)
    return entryDate.toDateString() === today.toDateString()
  })
  
  const todayClockIns = clockEntries.filter(c => {
    const today = new Date()
    const entryDate = new Date(c.timestamp)
    return entryDate.toDateString() === today.toDateString() && c.type === 'clock-in'
  })
  
  const pendingSync = deliveries.filter(d => d.status === 'pending').length +
    clockEntries.filter(c => c.status === 'pending').length

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl font-medium text-gray-900">Admin Dashboard</h1>
        <p className="text-sm text-gray-500">{formatDate(new Date())}</p>
      </div>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-4">
        <StatCard
          title="Today's Deliveries"
          value={todayDeliveries.length}
          icon={<Package className="w-5 h-5 text-gray-600" />}
        />
        <StatCard
          title="Active Drivers"
          value={todayClockIns.length}
          icon={<Users className="w-5 h-5 text-gray-600" />}
        />
        <StatCard
          title="Total Deliveries"
          value={deliveries.length}
          icon={<Package className="w-5 h-5 text-gray-600" />}
        />
        <StatCard
          title="Pending Sync"
          value={pendingSync}
          icon={<Clock className="w-5 h-5 text-gray-600" />}
        />
      </div>
      
      {/* Recent Activity */}
      <Card>
        <h2 className="font-medium text-gray-900 mb-4">Recent Deliveries</h2>
        {deliveries.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8">No deliveries yet</p>
        ) : (
          <div className="space-y-3">
            {deliveries.slice(0, 5).map((delivery) => (
              <div
                key={delivery.id}
                className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
              >
                <div className="flex items-center gap-3">
                  {delivery.photo && (
                    <img
                      src={delivery.photo}
                      alt="Delivery"
                      className="w-10 h-10 rounded-lg object-cover"
                    />
                  )}
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Driver: {delivery.driverId.slice(0, 8)}...
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(delivery.timestamp).toLocaleString()}
                    </p>
                  </div>
                </div>
                <Badge
                  variant={delivery.status === 'synced' ? 'success' : delivery.status === 'pending' ? 'warning' : 'error'}
                  size="sm"
                >
                  {delivery.status}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </Card>
      
      {/* Clock Entries */}
      <Card>
        <h2 className="font-medium text-gray-900 mb-4">Today's Clock Activity</h2>
        {clockEntries.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8">No clock entries yet</p>
        ) : (
          <div className="space-y-3">
            {clockEntries
              .filter(c => {
                const today = new Date()
                const entryDate = new Date(c.timestamp)
                return entryDate.toDateString() === today.toDateString()
              })
              .slice(0, 5)
              .map((entry) => (
                <div
                  key={entry.id}
                  className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
                >
                  <div className="flex items-center gap-3">
                    {entry.photo && (
                      <img
                        src={entry.photo}
                        alt="Clock entry"
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    )}
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        Driver: {entry.driverId.slice(0, 8)}...
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(entry.timestamp).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                  <Badge
                    variant={entry.type === 'clock-in' ? 'success' : 'secondary'}
                    size="sm"
                  >
                    {entry.type === 'clock-in' ? 'IN' : 'OUT'}
                  </Badge>
                </div>
              ))}
          </div>
        )}
      </Card>
    </div>
  )
}
